import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Button,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Breadcrumbs,
    Link
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { format } from 'date-fns';
import { QuestionPaper } from '../services/types/quiz';
import api from '../services/api';

type Order = 'asc' | 'desc';
type OrderBy = 'theme' | 'title' | 'description' | 'activation_date' | 'expiry_date';

interface HeadCell {
    id: OrderBy;
    label: string;
}

const headCells: HeadCell[] = [
    { id: 'theme', label: 'Theme' },
    { id: 'title', label: 'Question Paper Name' },
    { id: 'description', label: 'Description' },
    { id: 'activation_date', label: 'Activation Date' },
    { id: 'expiry_date', label: 'Expiry Date' },
];

const QuizzesList: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [papers, setPapers] = useState<QuestionPaper[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<OrderBy>('activation_date');

    const handleRequestSort = (property: OrderBy) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortPapers = (papers: QuestionPaper[]) => {
        return papers.sort((a, b) => {
            let valueA: any = orderBy === 'theme' ? a.theme?.title : a[orderBy];
            let valueB: any = orderBy === 'theme' ? b.theme?.title : b[orderBy];

            if (orderBy === 'activation_date' || orderBy === 'expiry_date') {
                valueA = new Date(valueA).getTime();
                valueB = new Date(valueB).getTime();
            }

            if (valueA === undefined || valueA === null) return order === 'asc' ? -1 : 1;
            if (valueB === undefined || valueB === null) return order === 'asc' ? 1 : -1;

            if (valueA < valueB) return order === 'asc' ? -1 : 1;
            if (valueA > valueB) return order === 'asc' ? 1 : -1;
            return 0;
        });
    };

    useEffect(() => {
        const fetchPapers = async () => {
            try {
                const response = await api.get('/quiz/public/papers');
                setPapers(response.data);
            } catch (err: any) {
                setError(err.response?.data?.error || 'Failed to load quizzes');
            } finally {
                setLoading(false);
            }
        };

        fetchPapers();
    }, []);

    const handleStartQuiz = async (paperId: number) => {
        navigate('/login', { 
            state: { 
                message: t('auth.pleaseLogin'),
                returnTo: '/',
                quizId: paperId
            } 
        });
    };

    const handleBackToHome = () => {
        navigate('/');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    const sortedPapers = sortPapers([...papers]);

    return (
        <Box sx={{ p: 4 }}>
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link
                        component="button"
                        onClick={handleBackToHome}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'inherit',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline'
                            }
                        }}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        {t('common.backToHome')}
                    </Link>
                    <Typography color="text.primary">{t('quiz.allAvailableQuizzes')}</Typography>
                </Breadcrumbs>
            </Box>
            
            <Typography variant="h4" gutterBottom>
                {t('quiz.allAvailableQuizzes')}
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ backgroundColor: 'primary.main' }}>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell key={headCell.id}>
                                    <TableSortLabel
                                        active={orderBy === headCell.id}
                                        direction={orderBy === headCell.id ? order : 'asc'}
                                        onClick={() => handleRequestSort(headCell.id)}
                                        sx={{
                                            color: 'primary.contrastText',
                                            '&:hover': {
                                                color: 'primary.contrastText',
                                            },
                                            '&.Mui-active': {
                                                color: 'primary.contrastText',
                                            },
                                            '& .MuiTableSortLabel-icon': {
                                                color: 'primary.contrastText !important',
                                            },
                                        }}
                                    >
                                        {t(`quiz.${headCell.id}`)}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            <TableCell sx={{ color: 'primary.contrastText' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedPapers.map((paper) => (
                            <TableRow key={paper.paper_id}>
                                <TableCell>{paper.theme?.title}</TableCell>
                                <TableCell>{paper.title}</TableCell>
                                <TableCell>{paper.description}</TableCell>
                                <TableCell>
                                    {format(new Date(paper.activation_date), t('quiz.dateFormat.full'))}
                                </TableCell>
                                <TableCell>
                                    {format(new Date(paper.expiry_date), t('quiz.dateFormat.full'))}
                                </TableCell>
                                <TableCell>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleStartQuiz(paper.paper_id)}
                                    >
                                        {t('quiz.start')}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default QuizzesList;
